import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery } from "convex/react";

import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { boardName } from "@/i18n/catalog";
import { Button } from "@/components/ui/button";
import { AddItemBar } from "@/components/board/add-item-bar";
import { AttachedThemes } from "@/components/board/attached-themes";
import { CuratePanel } from "@/components/board/curate-panel";
import { MasonryGrid } from "@/components/board/masonry-grid";
import { ItemDetail } from "@/components/board/item-detail";
import { Toaster } from "@/components/ui/sonner";
import { Splash } from "@/components/shell/splash";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { BackLink } from "@/components/shell/back-link";
import { RoomIcon } from "@/components/home/room-icon";
import { RoomPlanLine } from "@/components/board/room-plan-dialog";
import { useMirrorHotlinkedImages } from "@/components/board/use-mirror-hotlinked";

export const Route = createFileRoute("/boards/$boardId")({
  // ?item=<id> opens that find's detail sheet, for links from the first page.
  validateSearch: (search): { item?: string } => ({
    item: typeof search.item === "string" ? search.item : undefined,
  }),
  component: BoardPage,
});

function BoardPage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <SignedInBoard />
      </Authenticated>
      <Toaster />
    </>
  );
}

function SignedInBoard() {
  const { boardId } = useParams({ from: "/boards/$boardId" });
  const typedBoardId = boardId as Id<"boards">;
  const { t } = useTranslation("board");

  const board = useQuery(api.boards.get, { boardId: typedBoardId });
  const items = useQuery(api.items.listForBoard, { boardId: typedBoardId });
  const reactions = useQuery(api.reactions.listForBoard, {
    boardId: typedBoardId,
  });
  const currentUser = useQuery(api.auth.getCurrentUser);
  // A client of the team may be let add finds, or see the 3D, or neither.
  const me = useQuery(api.workspaces.getMyWorkspace);
  const can = me?.can;
  // The team, and in a design studio only roles that may change rooms.
  const isTeam = me?.role === "team" && me.can.rooms;

  const [pickedItemId, setPickedItemId] = useState<Id<"items"> | null>(null);
  useMirrorHotlinkedImages(items);

  // The URL can name a find to open (links from the first page). It counts
  // once the items are in, and is dropped when the sheet closes.
  const { item: requestedItemId } = useSearch({ from: "/boards/$boardId" });
  const navigate = useNavigate();
  const linkedItemId =
    requestedItemId && items?.some((it) => it._id === requestedItemId)
      ? (requestedItemId as Id<"items">)
      : null;
  const openItemId = pickedItemId ?? linkedItemId;
  function closeItem() {
    setPickedItemId(null);
    if (requestedItemId) {
      void navigate({
        to: "/boards/$boardId",
        params: { boardId },
        search: {},
        replace: true,
      });
    }
  }

  const reactionsByItem = useMemo(() => {
    const map = new Map<string, Doc<"reactions">[]>();
    (reactions ?? []).forEach((r) => {
      const list = map.get(r.itemId) ?? [];
      list.push(r);
      map.set(r.itemId, list);
    });
    return map;
  }, [reactions]);

  if (board === undefined) return <Splash subtitle={t("page.loadingBoard")} />;
  if (board === null) {
    return (
      <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="font-display italic text-xl text-ink-2">
          {t("page.notFound.title")}
        </p>
        <p className="text-sm text-ink-3">
          {t("page.notFound.body")}
        </p>
        <Button asChild size="sm" variant="outline">
          <Link to="/">{t("page.notFound.backHome")}</Link>
        </Button>
      </main>
    );
  }

  const currentUserId = currentUser?._id as string | undefined;

  return (
    <main className="mx-auto min-h-svh w-full max-w-[1400px] px-6 pb-32">
      {/* Quiet sticky top bar specific to this route */}
      <div className="sticky top-0 z-30 -mx-6 border-b border-line bg-paper/85 px-6 backdrop-blur-md">
        <div className="flex h-12 items-center justify-between gap-3">
          <BackLink to="/" label={t("page.allBoards")} />
          <div className="flex items-center gap-3 sm:gap-5">
            {can?.view3d ? (
              <Link
                to="/boards/$boardId/visit"
                params={{ boardId }}
                className="whitespace-nowrap text-2xs uppercase tracking-widest sm:tracking-[0.18em] text-ink-3 hover:text-ink transition-colors"
              >
                {t("page.visit3d")}
              </Link>
            ) : null}
            <Link
              to="/settings"
              className="whitespace-nowrap text-2xs uppercase tracking-widest sm:tracking-[0.18em] text-ink-3 hover:text-ink transition-colors"
            >
              {t("page.settings")}
            </Link>
          </div>
        </div>
      </div>

      {/* Board masthead */}
      <header className="grid gap-3 py-10 sm:py-14">
        <div className="flex items-center gap-2 text-2xs uppercase tracking-[0.18em] text-ink-3">
          <RoomIcon
            roomType={board.roomType}
            icon={board.icon}
            className="size-3.5 text-ink-3"
          />
          <span>{board.kind === "room" ? t("page.kind.room") : t("page.kind.theme")}</span>
          {items ? (
            <>
              <span className="text-ink-4">·</span>
              <span className="num">
                {t("page.itemCount", { count: items.length })}
              </span>
            </>
          ) : null}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-balance leading-[1.05] text-ink">
          <span className="italic">{boardName(board.name)}</span>
          <span className="text-accent">.</span>
        </h1>
        {board.description ? (
          <p className="max-w-prose text-sm text-ink-3 text-balance">
            {board.description}
          </p>
        ) : null}
        {board.kind === "room" ? <RoomPlanLine boardId={typedBoardId} className="pt-1" /> : null}
      </header>

      {can?.addItems ? <AddItemBar boardId={typedBoardId} /> : null}

      {!can?.addItems ? null : board.kind === "freeform" ? (
        <CuratePanel
          themeBoardId={typedBoardId}
          defaultOpen={items !== undefined && items.length === 0}
        />
      ) : isTeam ? (
        <AttachedThemes roomBoardId={typedBoardId} />
      ) : null}

      <div className="pt-6">
        {items === undefined ? (
          <p className="text-sm text-ink-3 num">{t("page.loadingItems")}</p>
        ) : (
          <MasonryGrid
            items={items}
            reactionsByItem={reactionsByItem}
            currentUserId={currentUserId}
            boardId={typedBoardId}
            onOpenItem={setPickedItemId}
          />
        )}
      </div>

      <ItemDetail
        open={openItemId !== null}
        onOpenChange={(next) => {
          if (!next) closeItem();
        }}
        items={items ?? []}
        initialItemId={openItemId}
        reactionsByItem={reactionsByItem}
        currentUserId={currentUserId}
        boardId={typedBoardId}
      />
    </main>
  );
}
